import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Modal, DatePicker, Empty, message, EchartsWithTheme, useTheme } from '@maxtropy/components';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import { Spin } from 'antd';
import styles from './index.module.scss';
import {
  apiV2AirCompressorOverviewConsumptionDataPost,
  apiV2AirCompressorOverviewMetricsPost,
  apiV2AirCompressorOverviewProductionDataPost,
  apiV2AirCompressorOverviewRatioDataPost,
} from '@maxtropy/device-customer-apis-v2';
import { formatNumber, formatValueWithWan, OVERVIEW_POLLING_INTERVAL } from '../../utils';

const { RangePicker } = DatePicker;

type DetailScopeType = 'overall' | 'station' | 'compressor';

type StationDetailModalProps = {
  open: boolean;
  onClose: () => void;
  stationTitle: string;
  scopeType: DetailScopeType;
  stationIds?: number[];
  compressorId?: number | string;
};

const formatXAxisLabel = (time: number, timeType: 'hour' | 'day' | 'month') => {
  if (!time) return '--';
  if (timeType === 'hour') return dayjs(time).format('HH:mm');
  if (timeType === 'day') return dayjs(time).format('MM-DD');
  return dayjs(time).format('YYYY-MM');
};

const formatTooltipTime = (time: number, timeType: 'hour' | 'day' | 'month') => {
  if (!time) return '--';
  if (timeType === 'hour') return dayjs(time).format('MM-DD HH:mm');
  if (timeType === 'day') return dayjs(time).format('MM-DD');
  return dayjs(time).format('YYYY-MM');
};

const StationDetailModal: React.FC<StationDetailModalProps> = ({
  open,
  onClose,
  stationTitle,
  scopeType,
  stationIds,
  compressorId,
}) => {
  const [metricType, setMetricType] = useState<'ratio' | 'production' | 'consumption'>('ratio');
  const [timeType, setTimeType] = useState<'hour' | 'day' | 'month'>('hour');
  const theme = useTheme();
  const getDefaultRange = (type: 'hour' | 'day' | 'month'): [dayjs.Dayjs, dayjs.Dayjs] => {
    const today = dayjs();
    if (type === 'hour') {
      return [today.startOf('day'), today.endOf('day')];
    }
    if (type === 'day') {
      return [today.subtract(30, 'day').startOf('day'), today.endOf('day')];
    }
    return [today.subtract(1, 'year').startOf('month'), today.endOf('month')];
  };
  const [rangeValue, setRangeValue] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(getDefaultRange('hour'));
  const invalidRangeRef = useRef(false);

  useEffect(() => {
    setRangeValue(getDefaultRange(timeType));
    invalidRangeRef.current = false;
  }, [timeType]);

  const resolvedStationIds = useMemo(() => {
    if (scopeType === 'compressor') return undefined;
    const ids = (stationIds ?? []).filter((id): id is number => typeof id === 'number');
    return ids.length ? ids : undefined;
  }, [scopeType, stationIds]);

  const resolvedCompressorId = useMemo(() => {
    if (scopeType !== 'compressor') return undefined;
    return compressorId;
  }, [compressorId, scopeType]);

  const queryPayload = useMemo(
    () => ({
      stationIds: resolvedStationIds,
      compressorId: resolvedCompressorId,
    }),
    [resolvedCompressorId, resolvedStationIds]
  );

  const { data: metricsData, loading: metricsLoading } = useRequest(
    async () => {
      if (!open) return undefined;
      const [todayData, monthData] = await Promise.all([
        apiV2AirCompressorOverviewMetricsPost({ ...queryPayload, timeType: 1 }),
        apiV2AirCompressorOverviewMetricsPost({ ...queryPayload, timeType: 2 }),
      ]);
      return { todayData, monthData };
    },
    {
      ready: open,
      pollingInterval: open ? OVERVIEW_POLLING_INTERVAL : undefined,
      refreshDeps: [open, queryPayload.stationIds?.join(','), String(queryPayload.compressorId ?? '')],
    }
  );

  const { data: chartData, loading: chartLoading } = useRequest(
    async () => {
      if (!open) return undefined;
      const payload = {
        ...queryPayload,
        startTime: rangeValue[0].startOf('day').valueOf(),
        endTime: rangeValue[1].endOf('day').valueOf(),
        timeResolution: timeType === 'hour' ? 'H' : timeType === 'day' ? 'D' : 'M',
      };

      if (metricType === 'ratio') {
        const res = await apiV2AirCompressorOverviewRatioDataPost(payload);
        return {
          totalValue: res.totalRatio,
          unit: 'kWh/m³',
          list: (res.ratioData ?? []).map(item => ({
            time: item.time,
            value: item.ratioValue,
          })),
        };
      }

      if (metricType === 'production') {
        const res = await apiV2AirCompressorOverviewProductionDataPost(payload);
        return {
          totalValue: res.totalProduction,
          unit: 'm³',
          list: (res.productionData ?? []).map(item => ({
            time: item.time,
            value: item.value,
          })),
        };
      }

      const res = await apiV2AirCompressorOverviewConsumptionDataPost(payload);
      return {
        totalValue: res.totalConsumption,
        unit: 'kWh',
        list: (res.consumptionData ?? []).map(item => ({
          time: item.time,
          value: item.value,
        })),
      };
    },
    {
      ready: open,
      pollingInterval: open ? OVERVIEW_POLLING_INTERVAL : undefined,
      refreshDeps: [
        open,
        metricType,
        timeType,
        rangeValue[0].valueOf(),
        rangeValue[1].valueOf(),
        queryPayload.stationIds?.join(','),
        String(queryPayload.compressorId ?? ''),
      ],
    }
  );

  const bars = useMemo(() => {
    return chartData?.list ?? [];
  }, [chartData?.list]);

  const summaryTitle =
    metricType === 'ratio' ? '总气电比(kWh/m³)' : metricType === 'production' ? '总产气量(m³)' : '总耗电量(kWh)';
  const summaryLabel = metricType === 'ratio' ? '气电比' : metricType === 'production' ? '流量' : '用电量';
  const isLoading = (metricsLoading && !metricsData) || (chartLoading && !chartData);
  const barColor = metricType === 'ratio' ? '#52E7FF' : metricType === 'production' ? '#FFCB47' : '#16DD8E';
  const metricUnit = metricType === 'ratio' ? 'kWh/m³' : metricType === 'production' ? 'm³' : 'kWh';
  const rangeDaysLimit = timeType === 'hour' ? 7 : timeType === 'day' ? 31 : 365;

  const disabledDate = (current: dayjs.Dayjs) => {
    return current.isAfter(dayjs().endOf('day'));
  };

  const rangeFormat = timeType === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD';
  const formatDetailMetric = (value?: number, unit?: string, maximumFractionDigits = 2) =>
    formatValueWithWan(value, unit, maximumFractionDigits).displayText;

  const onCalendarChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    _: [string, string],
    info?: { range?: 'start' | 'end' }
  ) => {
    if (info?.range !== 'end') {
      invalidRangeRef.current = false;
      return;
    }
    if (!dates?.[0] || !dates?.[1]) return;

    let invalid = false;
    let errorMsg = '';
    if (timeType === 'month') {
      invalid = dates[1].diff(dates[0], 'month') >= 12;
      errorMsg = '最多选择1年';
    } else {
      invalid = dates[1].diff(dates[0], 'day') >= rangeDaysLimit;
      errorMsg = timeType === 'hour' ? '最多选择7天' : '最多选择31天';
    }

    if (invalid) {
      if (!invalidRangeRef.current) {
        message.error(errorMsg);
      }
      invalidRangeRef.current = true;
      return;
    }

    invalidRangeRef.current = false;
  };

  const onRangeChange = (value: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (invalidRangeRef.current) {
      return;
    }
    if (value?.[0] && value?.[1]) {
      setRangeValue([value[0], value[1]]);
    }
  };

  const chartOption = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      grid: {
        left: 40,
        right: 16,
        top: 40,
        bottom: 0,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: Array<{ data?: number; marker?: string; dataIndex?: number }>) => {
          const first = params?.[0];
          const dataIndex = typeof first?.dataIndex === 'number' ? first.dataIndex : -1;
          const current = dataIndex >= 0 ? bars[dataIndex] : undefined;
          const title = formatTooltipTime(current?.time || 0, timeType);
          const value = typeof first?.data === 'number' ? first.data : 0;
          return `<div style="width:230px;">
            <div>${title}</div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
              <span style="display:inline-flex;align-items:center;">${first?.marker || ''}${summaryLabel}</span>
              <span>${
                metricType === 'ratio'
                  ? `${formatNumber(value, 4)} ${metricUnit}`
                  : `${formatDetailMetric(value, metricUnit, 2)} ${metricUnit}`
              }</span>
            </div>
          </div>`;
        },
      },
      xAxis: {
        type: 'category',
        data: bars.map(item => formatXAxisLabel(item.time!, timeType)),
        axisLine: {
          lineStyle: {
            color: 'rgba(255,255,255,0.2)',
          },
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: metricUnit,
      },
      series: [
        {
          type: 'bar',
          barWidth: 14,
          data: bars.map(item => (typeof item.value === 'number' && !Number.isNaN(item.value) ? item.value : 0)),
          itemStyle: {
            color: barColor,
          },
        },
      ],
    };
  }, [barColor, bars, metricType, metricUnit, summaryLabel, timeType]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      size="big"
      title={`${stationTitle}详情`}
      destroyOnClose
      className={styles.stationDetailModal}
      getContainer={() => document.querySelector('.compressorOverviewPage') || document.body}
    >
      <Spin spinning={isLoading}>
        <div className={styles.modalBody}>
          <div className={styles.summaryPanel}>
            <div className={styles.summaryGroup}>
              <div className={styles.summaryGroupTitle}>昨日</div>
              <div className={styles.summaryStats}>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>气电比(kWh/m³)</div>
                  <div className={styles.summaryValue}>{formatNumber(metricsData?.todayData?.ratio, 4)}</div>
                </div>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>耗电量(kWh)</div>
                  <div className={styles.summaryValue}>
                    {formatDetailMetric(metricsData?.todayData?.consumption, 'kWh', 2)}
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>产气量(m³)</div>
                  <div className={styles.summaryValue}>
                    {formatDetailMetric(metricsData?.todayData?.production, 'm³', 2)}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.summaryGroup}>
              <div className={styles.summaryGroupTitle}>上月</div>
              <div className={styles.summaryStats}>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>气电比(kWh/m³)</div>
                  <div className={styles.summaryValue}>{formatNumber(metricsData?.monthData?.ratio, 4)}</div>
                </div>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>耗电量(kWh)</div>
                  <div className={styles.summaryValue}>
                    {formatDetailMetric(metricsData?.monthData?.consumption, 'kWh', 2)}
                  </div>
                </div>
                <div className={styles.summaryItem}>
                  <div className={styles.summaryLabel}>产气量(m³)</div>
                  <div className={styles.summaryValue}>
                    {formatDetailMetric(metricsData?.monthData?.production, 'm³', 2)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.toolbar}>
            <div className={styles.segmentTabs}>
              <div
                className={classNames(styles.segmentButton, {
                  [styles.segmentButtonActive]: metricType === 'ratio',
                })}
                onClick={() => setMetricType('ratio')}
              >
                气电比
              </div>
              <div
                className={classNames(styles.segmentButton, {
                  [styles.segmentButtonActive]: metricType === 'production',
                })}
                onClick={() => setMetricType('production')}
              >
                产气量
              </div>
              <div
                className={classNames(styles.segmentButton, {
                  [styles.segmentButtonActive]: metricType === 'consumption',
                })}
                onClick={() => setMetricType('consumption')}
              >
                耗电量
              </div>
            </div>

            <div className={styles.rangeFilter}>
              <div className={styles.rangeTabs}>
                <div
                  className={classNames(styles.rangeButton, {
                    [styles.rangeButtonActive]: timeType === 'hour',
                  })}
                  onClick={() => setTimeType('hour')}
                >
                  按小时
                </div>
                <div
                  className={classNames(styles.rangeButton, {
                    [styles.rangeButtonActive]: timeType === 'day',
                  })}
                  onClick={() => setTimeType('day')}
                >
                  按日
                </div>
                <div
                  className={classNames(styles.rangeButton, {
                    [styles.rangeButtonActive]: timeType === 'month',
                  })}
                  onClick={() => setTimeType('month')}
                >
                  按月
                </div>
              </div>

              <RangePicker
                value={rangeValue}
                picker={timeType === 'month' ? 'month' : 'date'}
                format={rangeFormat}
                disabledDate={disabledDate}
                allowClear={false}
                onCalendarChange={onCalendarChange}
                onChange={onRangeChange}
              />
            </div>
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartMetaCards}>
              <div className={styles.chartMetaCard}>
                <div className={styles.chartMetaLabel}>{summaryTitle}</div>
                <div className={styles.chartMetaValue}>
                  {metricType === 'ratio'
                    ? formatNumber(chartData?.totalValue, 4)
                    : formatDetailMetric(chartData?.totalValue, metricUnit, 2)}
                </div>
              </div>
            </div>

            <div className={styles.chartArea}>
              {bars.length ? (
                <EchartsWithTheme option={chartOption} theme={theme} style={{ width: '100%', height: '100%' }} />
              ) : (
                <div className={styles.emptyWrap}>
                  <Empty />
                </div>
              )}
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default StationDetailModal;
